import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, ArrowRight, Calendar, Users, Package, Save, Search, Wine, X, Trash2, Plus, ChevronDown, Tag, Crown, Shield, Star, Filter, UserPlus, Eye, Bell, Mail, Pencil, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { Input } from '../components/SubscriptionAppInput';
import { Card } from '../components/SubscriptionAppCard';
import { EditorToolbar } from '../components/EditorToolbar';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Divider } from '../components/SubscriptionAppDivider';
// Types
interface AllocationFormData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  products: string[]; // Product IDs
  segments: string[]; // Segment IDs (Legacy)
  status: 'Draft' | 'Active' | 'Scheduled';
}
interface Product {
  id: string;
  name: string;
  variant: string;
  sku: string;
  price: number;
  stock: number;
}
interface SelectedProduct extends Product {
  allocationQty: number;
  minPerCustomer: number;
  maxPerCustomer: number;
  priceOverride: string; // empty string means use original
}
interface Rule {
  id: string;
  field: string;
  operator: string;
  value: string;
}
interface Member {
  id: string;
  name: string;
  email: string;
  tier: string;
}
const steps = [{
  id: 'details',
  label: 'Details',
  number: 1,
  icon: Calendar
}, {
  id: 'products',
  label: 'Products',
  number: 2,
  icon: Package
}, {
  id: 'eligibility',
  label: 'Eligibility',
  number: 3,
  icon: Users
}, {
  id: 'review',
  label: 'Review',
  number: 4,
  icon: Check
}];
// Mock Data
const availableProducts: Product[] = [{
  id: '1',
  name: '2023 Reserve Shiraz',
  variant: '750ml',
  sku: 'SHZ-23-RES',
  price: 85.0,
  stock: 480
}, {
  id: '2',
  name: '2022 Chardonnay',
  variant: '750ml',
  sku: 'CHD-22-STD',
  price: 45.0,
  stock: 320
}, {
  id: '3',
  name: '2018 Cabernet Sauvignon',
  variant: '750ml',
  sku: 'CAB-18-VNT',
  price: 120.0,
  stock: 120
}, {
  id: '4',
  name: 'Sparkling Rosé',
  variant: '750ml',
  sku: 'ROS-SPK-NV',
  price: 35.0,
  stock: 600
}, {
  id: '5',
  name: '2021 Pinot Noir',
  variant: '750ml',
  sku: 'PN-21-EST',
  price: 55.0,
  stock: 215
}, {
  id: '6',
  name: '2023 Sauvignon Blanc',
  variant: '750ml',
  sku: 'SB-23-CRL',
  price: 28.0,
  stock: 850
}, {
  id: '7',
  name: '2020 Merlot',
  variant: '750ml',
  sku: 'MER-20-RES',
  price: 42.0,
  stock: 150
}, {
  id: '8',
  name: 'Grand Reserve Box',
  variant: '6 Pack',
  sku: 'BOX-GR-6',
  price: 450.0,
  stock: 45
}];
const availableSegments = [{
  id: 'gold',
  name: 'Gold Members',
  count: 485
}, {
  id: 'silver',
  name: 'Silver Members',
  count: 850
}, {
  id: 'club',
  name: 'Club Members',
  count: 1200
}, {
  id: 'vip',
  name: 'VIPs',
  count: 150
}];
const mockMembers: Member[] = [{
  id: 'm1',
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  tier: 'Sommelier'
}, {
  id: 'm2',
  name: 'Michael Chen',
  email: 'michael.c@example.com',
  tier: 'Connoisseur'
}, {
  id: 'm3',
  name: 'Emma Williams',
  email: 'emma.w@example.com',
  tier: 'Vintner'
}, {
  id: 'm4',
  name: 'James Anderson',
  email: 'james.a@example.com',
  tier: 'Enthusiast'
}, {
  id: 'm5',
  name: 'Olivia Martinez',
  email: 'olivia.m@example.com',
  tier: 'Sommelier'
}, {
  id: 'm6',
  name: 'William Taylor',
  email: 'w.taylor@example.com',
  tier: 'Connoisseur'
}, {
  id: 'm7',
  name: 'Sophia Brown',
  email: 'sophia.b@example.com',
  tier: 'Enthusiast'
}, {
  id: 'm8',
  name: 'Liam Wilson',
  email: 'liam.w@example.com',
  tier: 'Vintner'
}];
const availableTags = ['VIP', 'Premium', 'Cellar Door Signup', 'Wine Club 2024', 'Event Attendee', 'Birthday Month', 'Referral', 'High Value'];
const tiers = [{
  id: 'enthusiast',
  name: 'Enthusiast',
  color: '#10B981',
  bg: '#D1FAE5'
}, {
  id: 'connoisseur',
  name: 'Connoisseur',
  color: '#3B82F6',
  bg: '#DBEAFE'
}, {
  id: 'sommelier',
  name: 'Sommelier',
  color: '#8B5CF6',
  bg: '#EDE9FE'
}, {
  id: 'vintner',
  name: 'Vintner',
  color: '#F59E0B',
  bg: '#FEF3C7'
}];
export function CreateAllocationPage() {
  const navigate = useNavigate();
  const {
    id: editId
  } = useParams<{
    id: string;
  }>();
  const isEditMode = Boolean(editId);
  const [activeStep, setActiveStep] = useState<number>(1);
  const [formData, setFormData] = useState<AllocationFormData>({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    products: [],
    segments: [],
    status: 'Draft'
  });
  // Product Selection State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([{
    ...availableProducts.find(p => p.id === '1')!,
    allocationQty: 200,
    minPerCustomer: 1,
    maxPerCustomer: 6,
    priceOverride: '75.00'
  }, {
    ...availableProducts.find(p => p.id === '3')!,
    allocationQty: 50,
    minPerCustomer: 1,
    maxPerCustomer: 3,
    priceOverride: ''
  }]);
  // Eligibility State
  const [activeMethods, setActiveMethods] = useState<Set<string>>(new Set(['tier']));
  const [selectedTiers, setSelectedTiers] = useState<string[]>(['sommelier', 'vintner']);
  const [earlyAccessEnabled, setEarlyAccessEnabled] = useState(false);
  const [earlyAccessTier, setEarlyAccessTier] = useState<string>('vintner');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [rules, setRules] = useState<Rule[]>([{
    id: '1',
    field: 'Subscription Status',
    operator: 'is',
    value: 'Active'
  }, {
    id: '2',
    field: 'Lifetime Spend',
    operator: 'greater than',
    value: '$500'
  }]);
  const [rulesLogic, setRulesLogic] = useState<'AND' | 'OR'>('AND');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  // Review Step State
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [notifyInvitation, setNotifyInvitation] = useState(true);
  const [notifyReminder, setNotifyReminder] = useState(false);
  const [reminderDays, setReminderDays] = useState(3);
  const [notifyConfirmation, setNotifyConfirmation] = useState(true);
  const [notifyAdminClaimed, setNotifyAdminClaimed] = useState(true);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  // Sync selected products to formData
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      products: selectedProducts.map(p => p.id)
    }));
  }, [selectedProducts]);
  // Handlers
  const handleNext = () => {
    if (activeStep < steps.length) {
      setActiveStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (activeStep > 1) {
      setActiveStep(prev => prev - 1);
    }
  };
  const handleStepClick = (stepNumber: number) => {
    if (stepNumber < activeStep || stepNumber === activeStep + 1) {
      setActiveStep(stepNumber);
    }
  };
  const handlePublish = () => {
    setShowPublishModal(true);
  };
  const confirmPublish = () => {
    console.log('Publishing allocation:', {
      ...formData,
      notifications: {
        invitation: notifyInvitation,
        reminder: notifyReminder ? reminderDays : false,
        confirmation: notifyConfirmation,
        adminClaimed: notifyAdminClaimed
      },
      template: saveAsTemplate ? templateName : null
    });
    setShowPublishModal(false);
    setTimeout(() => {
      navigate('/allocations');
    }, 500);
  };
  const handleSaveDraft = () => {
    console.log('Saving draft:', formData);
    navigate('/allocations');
  };
  const updateFormData = (field: keyof AllocationFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // Product Handlers
  const handleAddProduct = (product: Product) => {
    setSelectedProducts(prev => [...prev, {
      ...product,
      allocationQty: 0,
      minPerCustomer: 1,
      maxPerCustomer: 1,
      priceOverride: ''
    }]);
    setSearchQuery('');
  };
  const handleRemoveProduct = (productId: string) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };
  const handleUpdateProduct = (productId: string, field: keyof SelectedProduct, value: any) => {
    setSelectedProducts(prev => prev.map(p => {
      if (p.id === productId) {
        return {
          ...p,
          [field]: value
        };
      }
      return p;
    }));
  };
  // Eligibility Handlers
  const toggleMethod = (method: string) => {
    setActiveMethods(prev => {
      const newSet = new Set(prev);
      if (newSet.has(method)) {
        newSet.delete(method);
      } else {
        newSet.add(method);
      }
      return newSet;
    });
  };
  const toggleTier = (tierId: string) => {
    setSelectedTiers(prev => prev.includes(tierId) ? prev.filter(id => id !== tierId) : [...prev, tierId]);
  };
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    setTagDropdownOpen(false);
  };
  const addRule = () => {
    const newRule: Rule = {
      id: Math.random().toString(36).substr(2, 9),
      field: 'Lifetime Spend',
      operator: 'greater than',
      value: ''
    };
    setRules([...rules, newRule]);
  };
  const removeRule = (id: string) => {
    setRules(rules.filter(r => r.id !== id));
  };
  const updateRule = (id: string, field: keyof Rule, value: string) => {
    setRules(rules.map(r => r.id === id ? {
      ...r,
      [field]: value
    } : r));
  };
  const addMember = (member: Member) => {
    if (!selectedMembers.find(m => m.id === member.id)) {
      setSelectedMembers([...selectedMembers, member]);
    }
    setMemberSearchQuery('');
  };
  const removeMember = (id: string) => {
    setSelectedMembers(selectedMembers.filter(m => m.id !== id));
  };
  // Render Steps
  const renderDetailsStep = () => <div className="max-w-2xl mx-auto space-y-6" data-id="element-8295" data-mp="g1xm3j3t417l">
      <Card data-id="element-8296" data-mp="g38md8y1mqty">
        <div className="p-6 space-y-6" data-id="element-8297" data-mp="g0mhjh2jepff">
          <div data-id="element-8298" data-mp="b2l010krey62">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-4" data-id="element-8299" data-mp="b22u1c8ap5ky">
              Allocation Details
            </h2>
            <div className="space-y-4" data-id="element-8300" data-mp="641civqipebl">
              <div data-id="element-8301" data-mp="14y6p92pr42k">
                <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-8302" data-mp="15g80k48g8hc">
                  Allocation Name
                </label>
                <Input value={formData.name} onChange={e => updateFormData('name', e.target.value)} placeholder="e.g. 2024 Summer Release" data-id="element-8303" data-mp="3sikq4q7o4ph0" />
              </div>
              <div data-id="element-8304" data-mp="3jjz1qefimg8b">
                <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-8305" data-mp="3jkqvdjpjy3vy">
                  Description
                </label>
                <textarea className="w-full rounded-md border border-[#CBD5E1] p-3 text-sm focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] outline-none min-h-[100px]" value={formData.description} onChange={e => updateFormData('description', e.target.value)} placeholder="Describe this allocation for your members..." data-id="element-8306" data-mp="3elxfxlqpg2t5" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-8307" data-mp="39gaimz7dd2wd">
                <div data-id="element-8308" data-mp="3eeby7a5l6k33">
                  <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-8309" data-mp="3eevhau6z3eip">
                    Start Date
                  </label>
                  <Input type="date" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} data-id="element-8310" data-mp="2gs4s1vtmwjom" />
                </div>
                <div data-id="element-8311" data-mp="2qp15mfye3w01">
                  <label className="block text-sm font-medium text-[#0F172A] mb-2" data-id="element-8312" data-mp="2qnq30jgokuai">
                    End Date
                  </label>
                  <Input type="date" value={formData.endDate} onChange={e => updateFormData('endDate', e.target.value)} data-id="element-8313" data-mp="2vhkjle0zot5d" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
  const renderProductsStep = () => {
    const filteredProducts = searchQuery ? availableProducts.filter(p => (p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase())) && !selectedProducts.some(sp => sp.id === p.id)) : [];
    const totalAllocation = selectedProducts.reduce((sum, p) => sum + (Number(p.allocationQty) || 0), 0);
    return <div className="max-w-3xl mx-auto space-y-6" data-id="element-8314" data-mp="1bzmywu95b5al">
        <Card padding="none" data-id="element-8315" data-mp="1c051s9qbchrh">
          <div className="p-6 space-y-6" data-id="element-8316" data-mp="1c1g4e680vjh0">
            <div className="space-y-4" data-id="element-8317" data-mp="171bp2jiuousd">
              <div className="relative" data-id="element-8318" data-mp="172d1osfilpc7">
                <Input placeholder="Search products by name or SKU..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-8320" data-mp="1gxoidgahh6ml" />} data-id="element-8319" data-mp="1lyc3jlc16t8w" />
              </div>

              {searchQuery && <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#E2E8F0] overflow-hidden bg-white shadow-sm" data-id="element-8321" data-mp="2xc6ghtfjxkke">
                  {filteredProducts.length > 0 ? filteredProducts.map(product => <div key={product.id} onClick={() => handleAddProduct(product)} className="flex items-center gap-4 p-3 hover:bg-[#F8FAFC] cursor-pointer transition-colors" data-id="element-8322" data-mp="2zx4gte5l7csa">
                        <div className="w-10 h-10 rounded bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center flex-shrink-0" data-id="element-8323" data-mp="2qwz890onq8cj">
                          <Wine className="w-5 h-5 text-[#94A3B8]" data-id="element-8324" data-mp="2uz2pk5y0c75q" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-8325" data-mp="3ip6oi0093iqe">
                          <div className="flex items-center gap-2" data-id="element-8326" data-mp="3nmp0jz9kee2m">
                            <span className="font-medium text-[#0F172A] text-sm" data-id="element-8327" data-mp="3np2s3y51d85f">
                              {product.name}
                            </span>
                            <span className="text-xs text-[#64748B]" data-id="element-8328" data-mp="3dw3w3v77mfbs">
                              {product.variant}
                            </span>
                          </div>
                          <div className="text-xs text-[#64748B]" data-id="element-8329" data-mp="a8bvdc999cpi">
                            SKU: {product.sku} • Stock: {product.stock}
                          </div>
                        </div>
                        <div className="text-sm font-medium text-[#0EA5E9]" data-id="element-8330" data-mp="2dps802a9cgtq">
                          Add
                        </div>
                      </div>) : <div className="p-4 text-center text-sm text-[#64748B]" data-id="element-8331" data-mp="28t1i6n8ni6v4">
                      No products found matching "{searchQuery}"
                    </div>}
                </div>}
            </div>

            <Divider data-id="element-8332" data-mp="31hqkgplg0i8g" />

            <div data-id="element-8333" data-mp="2wgt7crfaiedt">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-4" data-id="element-8334" data-mp="2wg9mp2y99bw3">
                Selected Products ({selectedProducts.length})
              </h3>

              {selectedProducts.length > 0 ? <div className="space-y-3" data-id="element-8335" data-mp="2nor8gxkvd65n">
                  {selectedProducts.map(product => <div key={product.id} className="border border-[#E2E8F0] rounded-lg p-4 flex flex-col md:flex-row gap-4 items-start md:items-center bg-white" data-id="element-8336" data-mp="3k63ydh6u8uer">
                      <div className="flex items-center gap-4 flex-1 min-w-[240px]" data-id="element-8337" data-mp="3fdug8nbysyn1">
                        <div className="w-12 h-12 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center flex-shrink-0" data-id="element-8338" data-mp="11y2obi6k4vbl">
                          <Wine className="w-6 h-6 text-[#94A3B8]" data-id="element-8339" data-mp="1axfofk8sk7jn" />
                        </div>
                        <div data-id="element-8340" data-mp="1fwiun80icjt7">
                          <div className="font-semibold text-[#0F172A] text-sm" data-id="element-8341" data-mp="1fwslextdmuzy">
                            {product.name}
                          </div>
                          <div className="text-[13px] text-[#64748B]" data-id="element-8342" data-mp="1ktst1d5vk5t6">
                            {product.variant}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-end gap-4" data-id="element-8343" data-mp="1unswzbpf750b">
                        <div data-id="element-8344" data-mp="1ym8p1u0cz3kl">
                          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-8345" data-mp="23m4ulxopf329">
                            Allocation Qty
                          </label>
                          <Input type="number" value={product.allocationQty.toString()} onChange={e => handleUpdateProduct(product.id, 'allocationQty', parseInt(e.target.value) || 0)} className="w-24" data-id="element-8346" data-mp="28lhimlrbag6f" />
                          <div className="text-[11px] text-[#64748B] mt-1" data-id="element-8347" data-mp="3hsjouz2vmzt">
                            {product.stock} available
                          </div>
                        </div>

                        <div data-id="element-8348" data-mp="dgqgsmakkouf">
                          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-8349" data-mp="r9g5wzlea9vr">
                            Per Customer
                          </label>
                          <div className="flex items-center gap-2" data-id="element-8350" data-mp="n9xj1ya0joqa">
                            <div className="w-16" data-id="element-8351" data-mp="116b0x8igls5a">
                              <Input type="number" placeholder="Min" value={product.minPerCustomer.toString()} onChange={e => handleUpdateProduct(product.id, 'minPerCustomer', parseInt(e.target.value) || 0)} data-id="element-8352" data-mp="w8j88c9zq201" />
                            </div>
                            <span className="text-[#94A3B8]" data-id="element-8353" data-mp="2y0xluz5i4kan">-</span>
                            <div className="w-16" data-id="element-8354" data-mp="1di4xhkfd95ow">
                              <Input type="number" placeholder="Max" value={product.maxPerCustomer.toString()} onChange={e => handleUpdateProduct(product.id, 'maxPerCustomer', parseInt(e.target.value) || 0)} data-id="element-8355" data-mp="1din5febe58cn" />
                            </div>
                          </div>
                          <div className="text-[11px] text-[#64748B] mt-1 invisible" data-id="element-8356" data-mp="18q3ihqqtd0v6">
                            Placeholder
                          </div>
                        </div>

                        <div data-id="element-8357" data-mp="zqqf9fta98iw">
                          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-8358" data-mp="zs1hvcazsa8f">
                            Price Override
                          </label>
                          <div className="w-32" data-id="element-8359" data-mp="ptbzlif6onrn">
                            <Input placeholder={`Original: $${product.price.toFixed(2)}`} value={product.priceOverride} onChange={e => handleUpdateProduct(product.id, 'priceOverride', e.target.value)} icon={<span className="text-[#94A3B8] text-sm" data-id="element-8361" data-mp="2k5in9gp9kx0u">
                                  $
                                </span>} data-id="element-8360" data-mp="pu3wqxofitk1" />
                          </div>
                          <div className="text-[11px] text-[#64748B] mt-1 invisible" data-id="element-8362" data-mp="12z6jtfmbstjk">
                            Placeholder
                          </div>
                        </div>

                        <div className="pb-6" data-id="element-8363" data-mp="t70wwkaxl2b8">
                          <IconButton icon={<X className="w-4 h-4" data-id="element-8365" data-mp="o8plw68wglrf" />} onClick={() => handleRemoveProduct(product.id)} variant="ghost" className="text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2]" data-id="element-8364" data-mp="t67vzzynl710" />
                        </div>
                      </div>
                    </div>)}

                  <div className="text-[14px] text-[#64748B] pt-2 px-1" data-id="element-8366" data-mp="fadlxdm58bmb">
                    Total allocation:{' '}
                    <span className="font-medium text-[#0F172A]" data-id="element-8367" data-mp="abj2c9gcqj15">
                      {totalAllocation} units
                    </span>{' '}
                    across {selectedProducts.length} products
                  </div>
                </div> : <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-[#E2E8F0] rounded-lg bg-[#F8FAFC]" data-id="element-8368" data-mp="2c7i7kpyse108">
                  <div className="w-12 h-12 rounded-full bg-[#E2E8F0] flex items-center justify-center mb-3" data-id="element-8369" data-mp="2h3gtm6wn76xh">
                    <Package className="w-6 h-6 text-[#94A3B8]" data-id="element-8370" data-mp="2qvmrtydhmkl4" />
                  </div>
                  <p className="text-[14px] text-[#94A3B8] italic" data-id="element-8371" data-mp="2qxh46oap14dl">
                    Search above to add products to this allocation.
                  </p>
                </div>}
            </div>
          </div>
        </Card>
      </div>;
  };
  const renderEligibilityStep = () => {
    // Calculate mock member count based on selections
    const baseCount = 485;
    let count = baseCount;
    if (activeMethods.has('tier')) count = Math.min(count, selectedTiers.length * 120);
    if (activeMethods.has('tag')) count = Math.min(count, selectedTags.length * 50 || count);
    if (activeMethods.has('manual')) count += selectedMembers.length;
    return <div className="max-w-3xl mx-auto space-y-6" data-id="element-8372" data-mp="xs6a7dzp60y2">
        <Card padding="none" data-id="element-8373" data-mp="12sc470tfvzjb">
          <div className="p-6 space-y-6" data-id="element-8374" data-mp="12riubqz6otwy">
            {/* Header */}
            <div data-id="element-8375" data-mp="17j0uictduqgv">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-1" data-id="element-8376" data-mp="17iiquv4e59yx">
                Member Eligibility
              </h2>
              <p className="text-sm text-[#64748B]" data-id="element-8377" data-mp="1bjcdzma0ekqr">
                Define who can access this allocation
              </p>
            </div>

            {/* Method Tabs */}
            <div className="flex flex-wrap gap-2" data-id="element-8378" data-mp="jsy5y6oog2n0">
              {[{
              id: 'tier',
              label: 'By Tier'
            }, {
              id: 'tag',
              label: 'By Tag'
            }, {
              id: 'rules',
              label: 'Custom Rules'
            }, {
              id: 'manual',
              label: 'Manual Select'
            }].map(method => {
              const isActive = activeMethods.has(method.id);
              return <button key={method.id} onClick={() => toggleMethod(method.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${isActive ? 'bg-[#0EA5E9] text-white border-[#0EA5E9]' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC] hover:text-[#0F172A]'}`} data-id="element-8379" data-mp="24be4qyypxzfz">
                    {method.label}
                  </button>;
            })}
            </div>

            <Divider data-id="element-8380" data-mp="20k0k9xphpzia" />

            {/* Content Sections */}
            <div className="space-y-8" data-id="element-8381" data-mp="1vobkbt9k6glg">
              {/* BY TIER */}
              {activeMethods.has('tier') && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-8382" data-mp="1qm2perd4l4gp">
                  <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" data-id="element-8383" data-mp="1lp2tvadwjk3u">
                    Loyalty Tiers
                  </h3>
                  <div className="flex flex-wrap gap-3" data-id="element-8384" data-mp="3g2buecogzv9y">
                    {tiers.map(tier => {
                  const isSelected = selectedTiers.includes(tier.id);
                  return <button key={tier.id} onClick={() => toggleTier(tier.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${isSelected ? 'text-white border-transparent shadow-sm' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]'}`} style={{
                    backgroundColor: isSelected ? tier.color : undefined
                  }} data-id="element-8385" data-mp="3bz5rnf0kgm88">
                          {isSelected && <Check className="w-3.5 h-3.5" data-id="element-8386" data-mp="1s4kuosh50mxn" />}
                          {tier.name}
                        </button>;
                })}
                  </div>

                  {/* Early Access */}
                  <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0] mt-4" data-id="element-8387" data-mp="1j3376p1ehiyx">
                    <div className="flex items-start gap-3" data-id="element-8388" data-mp="1o2g11tqovqb0">
                      <div className="flex items-center h-5" data-id="element-8389" data-mp="7ozdzl2mzyux">
                        <input id="earlyAccess" type="checkbox" checked={earlyAccessEnabled} onChange={e => setEarlyAccessEnabled(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8390" data-mp="7neklys26lyn" />
                      </div>
                      <div className="flex-1" data-id="element-8391" data-mp="5zxbn6k8lf9">
                        <label htmlFor="earlyAccess" className="text-sm font-medium text-[#0F172A] block" data-id="element-8392" data-mp="56yr9hr9287">
                          Enable early access for top tier
                        </label>
                        <p className="text-xs text-[#64748B] mt-1" data-id="element-8393" data-mp="96y0k396cows">
                          Selected top tier gets access 48 hours before start
                          date
                        </p>

                        {earlyAccessEnabled && <div className="mt-3 max-w-xs" data-id="element-8394" data-mp="3rbri071kj5uo">
                            <select value={earlyAccessTier} onChange={e => setEarlyAccessTier(e.target.value)} className="w-full h-9 px-3 text-sm bg-white border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9]" data-id="element-8395" data-mp="3rdcei27g0yv6">
                              {tiers.filter(t => selectedTiers.includes(t.id)).map(tier => <option key={tier.id} value={tier.id} data-id="element-8396" data-mp="2tujca0jszfq9">
                                    {tier.name}
                                  </option>)}
                            </select>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>}

              {/* BY TAG */}
              {activeMethods.has('tag') && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-8397" data-mp="2axoqlsejks1r">
                  <div className="flex items-center justify-between" data-id="element-8398" data-mp="2fv8g5ogowr90">
                    <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" data-id="element-8399" data-mp="2kticw3i2bzwq">
                      Member Tags
                    </h3>
                  </div>

                  <div className="relative" data-id="element-8400" data-mp="2tvj70ri025u6">
                    <div className="w-full min-h-[40px] px-3 py-2 bg-white border border-[#CBD5E1] rounded-md flex items-center flex-wrap gap-2 cursor-pointer" onClick={() => setTagDropdownOpen(!tagDropdownOpen)} data-id="element-8401" data-mp="2tugfchcurzd7">
                      {selectedTags.length === 0 && <span className="text-sm text-[#94A3B8]" data-id="element-8402" data-mp="ry8zygpojq6m">
                          Select tags...
                        </span>}
                      {selectedTags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 rounded bg-[#E0F2FE] text-[#0284C7] text-xs font-medium border border-[#BAE6FD]" data-id="element-8403" data-mp="n4wci9wqlpfg">
                          {tag}
                          <button onClick={e => {
                      e.stopPropagation();
                      toggleTag(tag);
                    }} className="ml-1.5 hover:text-[#0369A1]" data-id="element-8404" data-mp="e4801jmzrx4o">
                            <X className="w-3 h-3" data-id="element-8405" data-mp="15ve0k16mjwuh" />
                          </button>
                        </span>)}
                      <div className="ml-auto" data-id="element-8406" data-mp="wwso8nrqxe25">
                        <ChevronDown className="w-4 h-4 text-[#94A3B8]" data-id="element-8407" data-mp="ww0p52yxwy74" />
                      </div>
                    </div>

                    {tagDropdownOpen && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto p-2" data-id="element-8408" data-mp="3hn17rft39rqx">
                        {availableTags.map(tag => <div key={tag} onClick={() => toggleTag(tag)} className="flex items-center px-3 py-2 hover:bg-[#F8FAFC] rounded-md cursor-pointer text-sm text-[#0F172A]" data-id="element-8409" data-mp="3j2dpw7818ies">
                            <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center ${selectedTags.includes(tag) ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-8410" data-mp="3a38nuqx9i6xv">
                              {selectedTags.includes(tag) && <Check className="w-3 h-3 text-white" data-id="element-8411" data-mp="354fd369d0u1e" />}
                            </div>
                            {tag}
                          </div>)}
                      </div>}
                  </div>
                </div>}

              {/* CUSTOM RULES */}
              {activeMethods.has('rules') && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-8412" data-mp="2hf3529prqs0o">
                  <div className="flex items-center justify-between" data-id="element-8413" data-mp="2mdxuvvm5lgua">
                    <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" data-id="element-8414" data-mp="2mg0lohv4re02">
                      Custom Rules
                    </h3>
                  </div>

                  <div className="space-y-0" data-id="element-8415" data-mp="1xum6mtwgfsgl">
                    {rules.map((rule, index) => <div key={rule.id} data-id="element-8416" data-mp="2btvkhmx6uahe" data-mpi={index}>
                        <div className="flex items-center gap-3" data-id="element-8417" data-mp="2bqpw69wdvcmq">
                          <select value={rule.field} onChange={e => updateRule(rule.id, 'field', e.target.value)} className="flex-1 h-10 px-3 pr-8 bg-white border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_10px_center] bg-no-repeat" data-id="element-8418" data-mp="27qfqkyci6o8e">
                            <option data-id="element-8419" data-mp="fo1gfdjht5t3">Lifetime Spend</option>
                            <option data-id="element-8420" data-mp="fqnorfe7jpcd">Tenure</option>
                            <option data-id="element-8421" data-mp="jpwiik98zn7x">Subscription Status</option>
                            <option data-id="element-8422" data-mp="jobiif45zc2o">Region</option>
                            <option data-id="element-8423" data-mp="onxzmzn45cgr">Order Count</option>
                          </select>
                          <select value={rule.operator} onChange={e => updateRule(rule.id, 'operator', e.target.value)} className="w-40 h-10 px-3 pr-8 bg-white border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_10px_center] bg-no-repeat" data-id="element-8424" data-mp="3o5e3gr9ohdyq">
                            <option data-id="element-8425" data-mp="2rxhtv5y11zc5">is</option>
                            <option data-id="element-8426" data-mp="2rw6ncyd76dhc">is not</option>
                            <option data-id="element-8427" data-mp="2nwg0v6upejim">greater than</option>
                            <option data-id="element-8428" data-mp="2nvwk3tgsi166">less than</option>
                          </select>
                          {rule.field === 'Subscription Status' ? <select value={rule.value} onChange={e => updateRule(rule.id, 'value', e.target.value)} className="flex-1 h-10 px-3 pr-8 bg-white border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_10px_center] bg-no-repeat" data-id="element-8429" data-mp="2e6czai9inx8o">
                              <option data-id="element-8430" data-mp="1e33w9peiqbvr">Active</option>
                              <option data-id="element-8431" data-mp="19496g3i4vn25">Paused</option>
                              <option data-id="element-8432" data-mp="192pn2416kvw5">Cancelled</option>
                            </select> : <input type="text" value={rule.value} onChange={e => updateRule(rule.id, 'value', e.target.value)} className="flex-1 h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9]" placeholder="Value..." data-id="element-8433" data-mp="lg9berdshahr" />}
                          <button onClick={() => removeRule(rule.id)} className="p-2 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded-md transition-colors" data-id="element-8434" data-mp="qczsni1byc4r">
                            <Trash2 className="w-4 h-4" data-id="element-8435" data-mp="3ttyjcaza0ys7" />
                          </button>
                        </div>

                        {/* AND/OR toggle between consecutive rules */}
                        {index < rules.length - 1 && <div className="flex items-center justify-center py-2" data-id="element-8436" data-mp="2e5a4nn9sk6b8">
                            <div className="flex bg-[#F1F5F9] rounded-full p-0.5" data-id="element-8437" data-mp="1zg43vuvb2818">
                              <button onClick={() => setRulesLogic('AND')} className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${rulesLogic === 'AND' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-8438" data-mp="24d48e1cib2q8">
                                AND
                              </button>
                              <button onClick={() => setRulesLogic('OR')} className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${rulesLogic === 'OR' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-8439" data-mp="2j28s5myy9f0g">
                                OR
                              </button>
                            </div>
                          </div>}
                      </div>)}
                  </div>

                  <button onClick={addRule} className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] flex items-center gap-1" data-id="element-8440" data-mp="39csr9u1g3kft">
                    <Plus className="w-4 h-4" data-id="element-8441" data-mp="34l113km7bgq7" /> Add Rule
                  </button>
                </div>}

              {/* MANUAL SELECT */}
              {activeMethods.has('manual') && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-8442" data-mp="2vl4k8561zm5p">
                  <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" data-id="element-8443" data-mp="2qmks1n0v9e4w">
                    Manual Selection
                  </h3>

                  <div className="relative" data-id="element-8444" data-mp="2lngkf0tlbogo">
                    <Input placeholder="Search members by name or email..." value={memberSearchQuery} onChange={e => setMemberSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-8446" data-mp="jvbmn8petwy1" />} data-id="element-8445" data-mp="2gon7b9i7tzh1" />

                    {memberSearchQuery && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto divide-y divide-[#F1F5F9]" data-id="element-8447" data-mp="extmo7tdes26">
                        {mockMembers.filter(m => m.name.toLowerCase().includes(memberSearchQuery.toLowerCase()) || m.email.toLowerCase().includes(memberSearchQuery.toLowerCase())).filter(m => !selectedMembers.find(sm => sm.id === m.id)).map(member => <div key={member.id} onClick={() => addMember(member)} className="p-3 hover:bg-[#F8FAFC] cursor-pointer flex items-center justify-between" data-id="element-8448" data-mp="113uqgog8fqed">
                              <div data-id="element-8449" data-mp="8fyu5kuks0i4">
                                <div className="text-sm font-medium text-[#0F172A]" data-id="element-8450" data-mp="8h0g4kd4q7em">
                                  {member.name}
                                </div>
                                <div className="text-xs text-[#64748B]" data-id="element-8451" data-mp="iey374j95y5n">
                                  {member.email}
                                </div>
                              </div>
                              <span className="text-xs px-2 py-1 rounded bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]" data-id="element-8452" data-mp="1tt1yqf5hnuct">
                                {member.tier}
                              </span>
                            </div>)}
                      </div>}
                  </div>

                  {selectedMembers.length > 0 && <div className="space-y-2" data-id="element-8453" data-mp="3bs9btfkpaiva">
                      <div className="text-xs font-medium text-[#64748B]" data-id="element-8454" data-mp="36sd8lijtuvgs">
                        {selectedMembers.length} members manually added
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-8455" data-mp="3lnjfs8unt0bp">
                        {selectedMembers.map(member => <div key={member.id} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#E2E8F0] rounded-full shadow-sm" data-id="element-8456" data-mp="3gqax875lmu9x">
                            <div className="w-5 h-5 rounded-full bg-[#E0F2FE] text-[#0369A1] flex items-center justify-center text-xs font-bold" data-id="element-8457" data-mp="3qg4630ecwvah">
                              {member.name.charAt(0)}
                            </div>
                            <span className="text-sm text-[#0F172A]" data-id="element-8458" data-mp="3d7uelj6dn99">
                              {member.name}
                            </span>
                            <button onClick={() => removeMember(member.id)} className="text-[#94A3B8] hover:text-[#EF4444]" data-id="element-8459" data-mp="rw9gz2egvg1f">
                              <X className="w-3.5 h-3.5" data-id="element-8460" data-mp="cy8raq46m0fq" />
                            </button>
                          </div>)}
                      </div>
                    </div>}
                </div>}
            </div>
          </div>

          {/* Bottom Preview Box */}
          <div className="bg-[#E0F2FE] border-t border-[#BAE6FD] p-4 flex items-center justify-between" data-id="element-8461" data-mp="8xf2luix0hls">
            <div className="flex items-center gap-2 text-[#0369A1]" data-id="element-8462" data-mp="3qef3wumxhq1w">
              <Users className="w-4 h-4" data-id="element-8463" data-mp="3qc2exxir6ffc" />
              <span className="text-sm font-medium" data-id="element-8464" data-mp="3vcgmlegfzncu">
                {count} members match these criteria
              </span>
            </div>
            <button className="text-sm font-medium text-[#0284C7] hover:underline" data-id="element-8465" data-mp="3hea538vsvzzc">
              Preview Members
            </button>
          </div>
        </Card>
      </div>;
  };
  const renderReviewStep = () => <div className="max-w-3xl mx-auto space-y-6" data-id="element-8466" data-mp="1ryg0nwf6www7">
      <Card data-id="element-8467" data-mp="1n5d6pn6i3lfh">
        <div className="p-6 border-b border-[#E2E8F0]" data-id="element-8468" data-mp="1n4uyrtah7irq">
          <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-8469" data-mp="1i6a9uq0oclil">
            Review Allocation
          </h2>
        </div>
        <div className="p-6 space-y-8" data-id="element-8470" data-mp="1d88g9b5mh3sv">
          {/* Details Summary */}
          <div data-id="element-8471" data-mp="2auz1lyfubeho">
            <div className="flex items-center justify-between mb-3" data-id="element-8472" data-mp="2avqv93pvn25b">
              <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8473" data-mp="2asuxbfe14dgv">
                Details
              </h3>
              <button onClick={() => handleStepClick(1)} className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1 transition-colors" data-id="element-8474" data-mp="20w9tgxdqx8em">
                <Pencil className="w-3 h-3" data-id="element-8475" data-mp="phwb281rhl5z" /> Edit
              </button>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0] space-y-2" data-id="element-8476" data-mp="pfjlb8pri6id">
              <div className="flex justify-between" data-id="element-8477" data-mp="lingx22t7l9w">
                <span className="text-sm text-[#64748B]" data-id="element-8478" data-mp="2x4nwz8ol6p5y">Name</span>
                <span className="text-sm font-medium text-[#0F172A]" data-id="element-8479" data-mp="314ydvgdx2zzl">
                  {formData.name || 'Untitled Allocation'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-8480" data-mp="36281qrlnr3lb">
                <span className="text-sm text-[#64748B]" data-id="element-8481" data-mp="3aw2sug3lekuf">Dates</span>
                <span className="text-sm font-medium text-[#0F172A]" data-id="element-8482" data-mp="3fue2oqtw0vcn">
                  {formData.startDate || 'TBD'} — {formData.endDate || 'TBD'}
                </span>
              </div>
            </div>
          </div>

          {/* Products Summary */}
          <div data-id="element-8483" data-mp="3ovloi9f7w3pu">
            <div className="flex items-center justify-between mb-3" data-id="element-8484" data-mp="3ospu2v2kvx65">
              <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8485" data-mp="3ttdfn181flsz">
                Products ({formData.products.length})
              </h3>
              <button onClick={() => handleStepClick(2)} className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1 transition-colors" data-id="element-8486" data-mp="1zfcicks4z6v0">
                <Pencil className="w-3 h-3" data-id="element-8487" data-mp="55num5zu1qtb" /> Edit
              </button>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] divide-y divide-[#E2E8F0]" data-id="element-8488" data-mp="14c9odrwltrg">
              {formData.products.length === 0 ? <div className="p-4 text-sm text-[#94A3B8] italic" data-id="element-8489" data-mp="9y8nn5awzzvs">
                  No products selected
                </div> : formData.products.map(id => {
              const product = availableProducts.find(p => p.id === id);
              return <div key={id} className="p-3 flex justify-between items-center" data-id="element-8490" data-mp="3eeji2l1ouu8f">
                      <span className="text-sm font-medium text-[#0F172A]" data-id="element-8491" data-mp="3sdj3zkxtgv0w">
                        {product?.name}
                      </span>
                      <span className="text-sm text-[#64748B]" data-id="element-8492" data-mp="17miohgyyshlb">
                        ${product?.price.toFixed(2)}
                      </span>
                    </div>;
            })}
            </div>
          </div>

          {/* Eligibility Summary */}
          <div data-id="element-8493" data-mp="2kk9htyntq0af">
            <div className="flex items-center justify-between mb-3" data-id="element-8494" data-mp="2kjpwe7yystrn">
              <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8495" data-mp="2pl6vpz1sw864">
                Eligibility
              </h3>
              <button onClick={() => handleStepClick(3)} className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1 transition-colors" data-id="element-8496" data-mp="2bmfngt9mdgpw">
                <Pencil className="w-3 h-3" data-id="element-8497" data-mp="34abebhciceer" /> Edit
              </button>
            </div>
            <div className="bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] p-4 space-y-4" data-id="element-8498" data-mp="389ka0rr3ymcy">
              {/* Active Methods */}
              <div className="flex flex-wrap gap-2" data-id="element-8499" data-mp="2uiypyzpx46dh">
                {Array.from(activeMethods).map(method => <span key={method} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0F172A] text-white" data-id="element-8500" data-mp="2zgh02tfo8rn2">
                    {method === 'tier' ? 'By Tier' : method === 'tag' ? 'By Tag' : method === 'rules' ? 'Custom Rules' : 'Manual Select'}
                  </span>)}
              </div>

              {/* Tiers */}
              {activeMethods.has('tier') && selectedTiers.length > 0 && <div data-id="element-8501" data-mp="3mozp6hvre1xt">
                  <div className="text-xs text-[#64748B] mb-2 uppercase tracking-wide font-medium" data-id="element-8502" data-mp="38rkrkpvcqnur">
                    Tiers
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-8503" data-mp="3ctepiqe5xdr8">
                    {selectedTiers.map(tierId => {
                  const tier = tiers.find(t => t.id === tierId);
                  return <span key={tierId} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border" style={{
                    backgroundColor: tier?.bg,
                    color: tier?.color,
                    borderColor: tier?.bg
                  }} data-id="element-8504" data-mp="9b4lp70vs700">
                          {tier?.name}
                        </span>;
                })}
                  </div>
                  {earlyAccessEnabled && <div className="mt-2 text-xs text-[#0EA5E9] flex items-center gap-1" data-id="element-8505" data-mp="s48kf6syha1h">
                      <Crown className="w-3 h-3" data-id="element-8506" data-mp="2crrqhkuh4ze6" />
                      Early access for{' '}
                      {tiers.find(t => t.id === earlyAccessTier)?.name}
                    </div>}
                </div>}

              {/* Tags */}
              {activeMethods.has('tag') && selectedTags.length > 0 && <div data-id="element-8507" data-mp="2hl43expsjhr3">
                  <div className="text-xs text-[#64748B] mb-2 uppercase tracking-wide font-medium" data-id="element-8508" data-mp="2hkkv88py07q9">
                    Tags
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-8509" data-mp="1ovncy5lqeaxi">
                    {selectedTags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 rounded bg-[#E0F2FE] text-[#0284C7] text-xs font-medium border border-[#BAE6FD]" data-id="element-8510" data-mp="23sedii755sop">
                        {tag}
                      </span>)}
                  </div>
                </div>}

              {/* Rules & Manual */}
              <div className="flex gap-6" data-id="element-8511" data-mp="3a8qgcwkwryqm">
                {activeMethods.has('rules') && <div data-id="element-8512" data-mp="3aat5lee8lnua">
                    <div className="text-xs text-[#64748B] mb-1 uppercase tracking-wide font-medium" data-id="element-8513" data-mp="3hbwydk85ocyg">
                      Rules
                    </div>
                    <div className="text-sm font-medium text-[#0F172A]" data-id="element-8514" data-mp="3m8wwn8yrkbf0">
                      {rules.length} active rules
                    </div>
                  </div>}
                {activeMethods.has('manual') && <div data-id="element-8515" data-mp="2rtvbuj3veq">
                    <div className="text-xs text-[#64748B] mb-1 uppercase tracking-wide font-medium" data-id="element-8516" data-mp="2yl6z05hsn8ac">
                      Manual
                    </div>
                    <div className="text-sm font-medium text-[#0F172A]" data-id="element-8517" data-mp="33krw0adel6kf">
                      {selectedMembers.length} members
                    </div>
                  </div>}
              </div>

              <div className="pt-3 border-t border-[#E2E8F0]" data-id="element-8518" data-mp="3dauwswqrnotb">
                <div className="flex items-center justify-between" data-id="element-8519" data-mp="2fv7e7uszkfcq">
                  <span className="text-sm text-[#64748B]" data-id="element-8520" data-mp="2fsuvvgjbg2z5">
                    Total Eligible Members
                  </span>
                  <span className="text-sm font-bold text-[#0EA5E9]" data-id="element-8521" data-mp="11z615kaamlsa">~485</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div data-id="element-8522" data-mp="sxzjh7vwr4xg">
            <h3 className="text-sm font-semibold text-[#64748B] uppercase tracking-wider mb-3" data-id="element-8523" data-mp="sxg7e7sxvarc">
              Notifications
            </h3>
            <div className="bg-white rounded-lg border border-[#E2E8F0] divide-y divide-[#E2E8F0]" data-id="element-8524" data-mp="nyd710nwlspp">
              {/* Invitation */}
              <div className="p-4 flex items-start gap-3" data-id="element-8525" data-mp="e93czf9i1zyy">
                <div className="flex items-center h-5 mt-0.5" data-id="element-8526" data-mp="e7iifpn6qh13">
                  <input id="notifyInvitation" type="checkbox" checked={notifyInvitation} onChange={e => setNotifyInvitation(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8527" data-mp="a6oskqq6ms5k" />
                </div>
                <div className="flex-1" data-id="element-8528" data-mp="8ivcmfbma71">
                  <label htmlFor="notifyInvitation" className="text-sm font-medium text-[#0F172A] block" data-id="element-8529" data-mp="3rf3eljz4wdti">
                    Send invitation email when allocation opens
                  </label>
                  <p className="text-xs text-[#64748B] mt-0.5" data-id="element-8530" data-mp="20ba53go4n92n">
                    Members receive an email with allocation details and a
                    purchase link
                  </p>
                </div>
              </div>

              {/* Reminder */}
              <div className="p-4 flex items-start gap-3" data-id="element-8531" data-mp="1qjnmv0ddg9qe">
                <div className="flex items-center h-5 mt-0.5" data-id="element-8532" data-mp="1ckxu1wxl4w7u">
                  <input id="notifyReminder" type="checkbox" checked={notifyReminder} onChange={e => setNotifyReminder(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8533" data-mp="1hksizuxsvbq9" />
                </div>
                <div className="flex-1" data-id="element-8534" data-mp="17nntpm8nr058">
                  <div className="flex items-center flex-wrap gap-2" data-id="element-8535" data-mp="17opeiifh7105">
                    <label htmlFor="notifyReminder" className="text-sm font-medium text-[#0F172A]" data-id="element-8536" data-mp="2x2lw3b2amp8y">
                      Send reminder before allocation closes
                    </label>
                    {notifyReminder && <div className="flex items-center gap-2 ml-2 animate-in fade-in duration-200" data-id="element-8537" data-mp="qbh8g5po0aqj">
                        <span className="text-sm text-[#0F172A]" data-id="element-8538" data-mp="lce30k5rw2i1">
                          Send reminder
                        </span>
                        <input type="number" value={reminderDays} onChange={e => setReminderDays(parseInt(e.target.value) || 1)} className="w-12 h-7 px-1 text-center text-sm border border-[#CBD5E1] rounded-md focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] outline-none" min="1" data-id="element-8539" data-mp="vc4xwmy8wjin" />
                        <span className="text-sm text-[#0F172A]" data-id="element-8540" data-mp="1j1z9z2aqq41u">
                          days before close
                        </span>
                      </div>}
                  </div>
                </div>
              </div>

              {/* Confirmation */}
              <div className="p-4 flex items-start gap-3" data-id="element-8541" data-mp="3ktuf0z2hrab3">
                <div className="flex items-center h-5 mt-0.5" data-id="element-8542" data-mp="3fvj7ww3kzdwk">
                  <input id="notifyConfirmation" type="checkbox" checked={notifyConfirmation} onChange={e => setNotifyConfirmation(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8543" data-mp="1jrcvaj5qqur3" />
                </div>
                <div className="flex-1" data-id="element-8544" data-mp="1sqhr3hpx49zk">
                  <label htmlFor="notifyConfirmation" className="text-sm font-medium text-[#0F172A] block" data-id="element-8545" data-mp="1xr4dkw3aixaw">
                    Send confirmation email on purchase
                  </label>
                </div>
              </div>

              {/* Admin Notification */}
              <div className="p-4 flex items-start gap-3" data-id="element-8546" data-mp="27nh97ziebl8a">
                <div className="flex items-center h-5 mt-0.5" data-id="element-8547" data-mp="2cfs6svpnyuxo">
                  <input id="notifyAdminClaimed" type="checkbox" checked={notifyAdminClaimed} onChange={e => setNotifyAdminClaimed(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8548" data-mp="2ghm3yu0nhgt3" />
                </div>
                <div className="flex-1" data-id="element-8549" data-mp="2qgl4tqf6c85j">
                  <label htmlFor="notifyAdminClaimed" className="text-sm font-medium text-[#0F172A] block" data-id="element-8550" data-mp="30jqeyefne7fl">
                    Notify admin when fully claimed
                  </label>
                  <p className="text-xs text-[#64748B] mt-0.5" data-id="element-8551" data-mp="2vmq9o5qmh8pj">
                    You'll receive a notification when all stock is allocated
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Template Section */}
          <div className="pt-6 border-t border-[#E2E8F0]" data-id="element-8552" data-mp="3j9pzqp4gh0d7">
            <div className="flex items-start gap-3" data-id="element-8553" data-mp="3eamtj1cqoo3n">
              <div className="flex items-center h-5 mt-0.5" data-id="element-8554" data-mp="3ebwsrtvbw2bv">
                <input id="saveAsTemplate" type="checkbox" checked={saveAsTemplate} onChange={e => setSaveAsTemplate(e.target.checked)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8555" data-mp="39jn0i76qqq6f" />
              </div>
              <div className="flex-1 space-y-3" data-id="element-8556" data-mp="5wmnye020j0e">
                <label htmlFor="saveAsTemplate" className="text-sm font-medium text-[#0F172A] block" data-id="element-8557" data-mp="143b5j96xrwn">
                  Save as template for future use
                </label>
                {saveAsTemplate && <div className="animate-in fade-in slide-in-from-top-1 duration-200" data-id="element-8558" data-mp="pjvmmsifaor8">
                    <Input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="e.g. Reserve Release Template" className="max-w-md" data-id="element-8559" data-mp="aupprb725ami" />
                    <p className="text-xs text-[#64748B] mt-1.5" data-id="element-8560" data-mp="ftu0455pxeef">
                      Saves product rules, eligibility, and notification
                      settings for reuse
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
  return <div className="h-screen flex flex-col bg-[#F1F5F9]" data-id="element-8561" data-mp="tnbdf87t9uw0">
      {/* Toolbar */}
      <div className="h-16 bg-white border-b border-[#E2E8F0] px-6 flex items-center justify-between flex-shrink-0 z-10" data-id="element-8562" data-mp="yi7c2r7jzgi3">
        <div className="flex items-center gap-4" data-id="element-8563" data-mp="3ja9crsh4xwt4">
          <Button variant="ghost" onClick={() => isEditMode ? navigate(`/allocations/${editId}`) : navigate('/allocations')} className="text-[#64748B]" data-id="element-8564" data-mp="3jb2ay5213dzn">
            Cancel
          </Button>
          <div className="h-6 w-px bg-[#E2E8F0]" data-id="element-8565" data-mp="1wci1nta2261x" />
          <h1 className="text-lg font-semibold text-[#0F172A]" data-id="element-8566" data-mp="1wfxj32r7br6i">
            {isEditMode ? 'Edit Allocation' : 'Create Allocation'}
          </h1>
        </div>

        <div className="flex items-center gap-3" data-id="element-8567" data-mp="26c8ulqiywczw">
          <Button variant="outline" onClick={handleSaveDraft} data-id="element-8568" data-mp="1ilwpenf3wud0">
            Save Draft
          </Button>
          {activeStep === 4 ? <Button variant="primary" onClick={handlePublish} iconLeft={<Save className="w-4 h-4" data-id="element-8570" data-mp="1dl8rriwdhf9t" />} data-id="element-8569" data-mp="1dnuyjgbfvqqz">
              {isEditMode ? 'Save Changes' : 'Publish Allocation'}
            </Button> : <Button variant="primary" onClick={handleNext} iconRight={<ArrowRight className="w-4 h-4" data-id="element-8572" data-mp="1mmgjfi4dvhux" />} data-id="element-8571" data-mp="1mn86u5ntu9a4">
              Next Step
            </Button>}
        </div>
      </div>

      {/* Step Progress Bar */}
      <div className="bg-white border-b border-[#E2E8F0]" data-id="element-8573" data-mp="ux3nmabszsah">
        <div className="flex items-center justify-center max-w-4xl mx-auto py-6 px-8" data-id="element-8574" data-mp="uurb4cotea4t">
          {steps.map((step, index) => {
          const status = activeStep > step.number ? 'completed' : activeStep === step.number ? 'active' : 'upcoming';
          const isLast = index === steps.length - 1;
          return <Fragment key={step.id} data-id="element-8575" data-mp="3f9rrsih34x6b" data-mpi={index}>
                <button onClick={() => handleStepClick(step.number)} className="flex items-center gap-3 transition-all duration-200 group" disabled={status === 'upcoming'} data-id="element-8576" data-mp="3adug4pgroezv">
                  {/* Step Circle */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold transition-all duration-200 ${status === 'completed' ? 'bg-[#10B981] text-white' : status === 'active' ? 'bg-[#0EA5E9] text-white' : 'bg-[#E2E8F0] text-[#94A3B8]'}`} data-id="element-8577" data-mp="6xnlf8gbxs38">
                    {status === 'completed' ? <Check className="w-5 h-5" data-id="element-8578" data-mp="2mp1p97f04xc4" /> : <step.icon className="w-5 h-5" data-id="element-8579" data-mp="2ml446g8v5vpl" />}
                  </div>

                  {/* Step Label */}
                  <span className={`text-base font-medium transition-colors duration-200 ${status === 'active' ? 'text-[#0F172A] font-semibold' : status === 'completed' ? 'text-[#475569]' : 'text-[#94A3B8]'}`} data-id="element-8580" data-mp="27kgpsf5bi5dv">
                    {step.label}
                  </span>
                </button>

                {/* Connector Line */}
                {!isLast && <div className={`flex-1 h-0.5 mx-4 max-w-[120px] transition-colors duration-200 ${status === 'completed' ? 'bg-[#10B981]' : 'bg-[#E2E8F0]'}`} data-id="element-8581" data-mp="2ma4tfn0z1g4r" />}
              </Fragment>;
        })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-[#F1F5F9] p-8" data-id="element-8582" data-mp="3094igvsebx1g">
        {activeStep === 1 && renderDetailsStep()}
        {activeStep === 2 && renderProductsStep()}
        {activeStep === 3 && renderEligibilityStep()}
        {activeStep === 4 && renderReviewStep()}
      </div>

      {/* Publish Confirmation Modal */}
      {showPublishModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-8583" data-mp="1av7qrabv6sex">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-8584" data-mp="12u9yrnaa5lep">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-8585" data-mp="1cs7ka30r943m">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-8586" data-mp="1cpblydkzwden">
                Publish Allocation
              </h3>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6" data-id="element-8587" data-mp="1lqrvyk7wedic">
              <div data-id="element-8588" data-mp="1gtjaa9njjrcc">
                <p className="text-base font-bold text-[#0F172A] mb-4" data-id="element-8589" data-mp="1gu1eptkcxbvc">
                  {formData.name || 'Untitled Allocation'}
                </p>

                <div className="space-y-3" data-id="element-8590" data-mp="1vk8shvzf66pp">
                  <div className="flex items-start gap-3" data-id="element-8591" data-mp="1qit53x9rro45">
                    <Calendar className="w-5 h-5 text-[#64748B] flex-shrink-0" data-id="element-8592" data-mp="1qlfbhtkxbxks" />
                    <span className="text-sm text-[#475569]" data-id="element-8593" data-mp="24j3v6y398yo9">
                      Active from {formData.startDate || 'TBD'} —{' '}
                      {formData.endDate || 'TBD'}
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-8594" data-mp="2o4duwzcrdvj3">
                    <Wine className="w-5 h-5 text-[#64748B] flex-shrink-0" data-id="element-8595" data-mp="2o3un4bgtonis" />
                    <span className="text-sm text-[#475569]" data-id="element-8596" data-mp="2a432hrvsw8vt">
                      {selectedProducts.reduce((sum, p) => sum + (Number(p.allocationQty) || 0), 0)}{' '}
                      units across {selectedProducts.length} products
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-8597" data-mp="2561npj5eqvq5">
                    <Users className="w-5 h-5 text-[#64748B] flex-shrink-0" data-id="element-8598" data-mp="1red9997orv60" />
                    <span className="text-sm text-[#475569]" data-id="element-8599" data-mp="1rg7s8hzedd7g">
                      485 eligible members
                    </span>
                  </div>
                </div>
              </div>

              <div className="h-px bg-[#E2E8F0]" data-id="element-8600" data-mp="1hgz2y2fh8mrf" />

              <div className="space-y-3" data-id="element-8601" data-mp="1mftohc4twpf8">
                <div className="flex items-start gap-3" data-id="element-8602" data-mp="1meihz4k013kf">
                  <Mail className="w-5 h-5 text-[#0EA5E9] flex-shrink-0" data-id="element-8603" data-mp="2osy61xcvnyf4" />
                  <span className="text-sm text-[#0F172A]" data-id="element-8604" data-mp="2kr3a78dpud8q">
                    Invitation emails will be sent to 485 members via Klaviyo
                  </span>
                </div>

                {earlyAccessEnabled && <div className="flex items-start gap-3 bg-[#E0F2FE] p-3 rounded-md border border-[#BAE6FD]" data-id="element-8605" data-mp="2b1jkpjvhzl4w">
                    <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0" data-id="element-8606" data-mp="262gtb28fxdfe" />
                    <span className="text-sm text-[#0369A1]" data-id="element-8607" data-mp="2133y9u7f101q">
                      {tiers.find(t => t.id === earlyAccessTier)?.name} tier
                      members will receive early access on{' '}
                      {formData.startDate ? new Date(new Date(formData.startDate).getTime() - 2 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short'
                }) : '2 days prior'}
                    </span>
                  </div>}
              </div>

              <div className="flex items-start gap-3 text-[#64748B]" data-id="element-8608" data-mp="3u0rak21ih857">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" data-id="element-8609" data-mp="2tok5cthz5w1" />
                <p className="text-xs" data-id="element-8610" data-mp="2ru000q1vht0">
                  Once published, the allocation window and eligibility rules
                  cannot be changed. Product quantities can still be adjusted.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex justify-end gap-3" data-id="element-8611" data-mp="3b2og37q72j00">
              <Button variant="secondary" onClick={() => setShowPublishModal(false)} data-id="element-8612" data-mp="3g0q9alhc3yp7">
                Cancel
              </Button>
              {formData.startDate && new Date(formData.startDate) > new Date() && <Button variant="secondary" onClick={confirmPublish} data-id="element-8613" data-mp="3p2g13sf4ozni">
                    Schedule for Start Date
                  </Button>}
              <Button variant="primary" onClick={confirmPublish} data-id="element-8614" data-mp="14anrki2buwjr">
                Publish Now
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}